import React from 'react';
import './styles.css';
import '../SharedUtils/c-buttons.css';
import '../FullWidthRichText/subtitle.css';
import '../FullWidthRichText/typography.css';
import RenderingStyling from '../../../lib/RenderingStyling';
import { AdsCtaLink } from '@arm/arm-design-system-react';
import { useSubtitleClasses } from '../FullWidthRichText/useSubtitleClasses';
import {
  addStyle,
  getTrackingAttributes,
  getTrackingSecondaryAttributes,
  getBackgroundImages,
} from '../SharedUtils/utils';
import {
  renderSubTitle,
  renderFeaturedHeading,
  renderSecondaryHeading,
  renderText,
  renderCtaLinkTheme,
} from '../FullWidthRichText/fieldsRenderer';
import { Placeholder, withSitecoreContext, Text } from '@sitecore-jss/sitecore-jss-react';

import { addBackgroundColorVariables } from '../SharedUtils/themeColors';
import { useInvertedColor, useTheme } from '../SharedUtils/hooks';
import useTwoCol from './twoColHook';

const FullWidthTwoColTextImage = ({ fields, rendering, sitecoreContext }) => {
  const getColumnRatioClass = (columnSize) => {
    switch (columnSize?.fields?.value?.value) {
      case '1:2':
        return 'is-40-60';
      case '2:1':
        return 'is-60-40';
      default:
        return 'is-50-50';
    }
  };

  useTwoCol();

  const componentId = `c-${rendering.uid}`;

  const widgetStyles = {};
  const widgetClasses = ['o-widget', 'c-full-width-two-col-text-img', 'background-color-theme'];

  const subTitleClasses = useSubtitleClasses('c-full-width-text-subcontent__subtitle', fields);

  const { isLight } = useTheme();

  addBackgroundColorVariables(fields, widgetStyles);

  const isColorInverted = useInvertedColor(fields);

  const showCross = fields.ShowCross?.value;

  const backgroundColorCustomLight = fields.BackgroundColourTextLight?.value;
  const backgroundColorCustomDark = fields.BackgroundColourTextDark?.value;

  const backgroundColorCustom = isLight ? backgroundColorCustomLight : backgroundColorCustomDark;

  addStyle(widgetStyles, '--full-width-two-col-text-img-padding-top', fields.PaddingTop?.value);
  addStyle(
    widgetStyles,
    '--full-width-two-col-text-img-padding-bottom',
    fields.PaddingBottom?.value
  );
  addStyle(widgetStyles, '--full-width-two-col-text-img-background-colour', backgroundColorCustom);

  const ratioClass = getColumnRatioClass(fields.ColumnsSize);

  if (ratioClass) widgetClasses.push(ratioClass);
  if (isColorInverted) widgetClasses.push('is-inverted');
  if (fields.IsOverlapped?.value) widgetClasses.push('is-overlapped');
  if (fields.Reverse?.value) widgetClasses.push('is-reversed');
  if (showCross) widgetClasses.push('has-cross');
  if (fields.TextAlignCentered?.value) widgetClasses.push('u-text-center');
  if (fields.DisplayImageOption?.value) widgetClasses.push('display-image');
  if (backgroundColorCustom) widgetClasses.push('u-custom-background-color');

  const backgroundImageStyle = {};

  const { largeBackgroundImageSrc } = getBackgroundImages(fields, isLight);

  if (largeBackgroundImageSrc) {
    backgroundImageStyle['backgroundImage'] = `url('${largeBackgroundImageSrc}')`;
  }

  return (
    <div
      id={componentId}
      className={widgetClasses.join(' ')}
      style={widgetStyles}
      {...RenderingStyling(rendering.componentName)}
      tabIndex="-1"
    >
      <div
        className="c-full-width-two-col-text-img__imagery"
        style={backgroundImageStyle}
        tabIndex="-1"
      >
        {showCross && <div className="c-full-width-two-col-text-img__imagery__cross" alt="arm" />}
      </div>

      <div className="c-full-width-two-col-text-img__content">
        <div className="c-container">
          <div className="c-full-width-two-col-text-img__content__fields" tabIndex="0">
            {renderTextFields({
              fields: fields,
              sitecoreContext: sitecoreContext,
              subTitleClasses: subTitleClasses,
              isLight,
            })}
          </div>
        </div>

        {fields.SubComponent?.value && (
          <div className="c-container">
            <div className="c-full-width-two-col-text-img__content__subcomponent">
              <Placeholder name="icon-panel" rendering={rendering} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withSitecoreContext()(FullWidthTwoColTextImage);

const mapCtaSizeClass = (sizeValue) => {
  if (!sizeValue) {
    return 'is-large';
  }

  return `is-${sizeValue}`;
};

const mapCtaThemeClass = (theme, startClass) => {
  if (!theme) {
    return `${startClass}-default`;
  }

  return `${startClass}-${theme}`;
};

const renderTextFields = ({ fields, sitecoreContext, subTitleClasses, isLight }) => {
  const isEditorMode = sitecoreContext?.pageEditing ?? false;

  const trackingAttributes = getTrackingAttributes(fields);

  const secondaryTrackingAttributes = getTrackingSecondaryAttributes(fields);

  const primarHighlighted = fields.HighlightPrimary?.value ? 'is-highlighted' : '';
  const secondaryHighlighted = fields.HighlightSecondary?.value ? 'is-highlighted' : '';

  const { largeBackgroundImageSrc, mediumBackgroundImageSrc, smallBackgroundImageSrc } =
    getBackgroundImages(fields, isLight);

  const renderFirstCta = () => {
    if (!sitecoreContext.pageEditing && !fields.CallToAction?.value?.href) {
      return null;
    }

    const isLinkCta = fields.UseLinkButton?.value;

    if (isLinkCta) {
      return (
        <AdsCtaLink
          link={fields.CallToAction.value.href}
          srLabel={fields.CallToAction.value.title}
          className="c-full-width-two-col-text-img__link"
          {...trackingAttributes}
        >
          {fields.CallToAction.value.text}
        </AdsCtaLink>
      );
    }

    const size = mapCtaSizeClass(fields.CTAPrimarySize?.fields?.value?.value);

    const themeValue = isLight
      ? fields.CTAPrimaryThemeLight?.fields?.value?.value
      : fields.CTAPrimaryThemeDark?.fields?.value?.value;
    const theme = mapCtaThemeClass(themeValue, 'c-button-primary');

    return renderCtaLinkTheme({
      field: fields.CallToAction,
      tracking: trackingAttributes,
      classes: `c-full-width-two-col-text-img__link ${size} ${theme}`,
    });
  };

  const renderPrimaryHeading = (fields) => {
    if (!isEditorMode && !fields.PrimaryHeading?.value) {
      return <></>;
    }

    switch (fields.PrimaryHeadlineType?.value) {
      case 'H1':
        return (
          <h1
            tabIndex="0"
            className={`c-full-width-two-col-text-img__content__fields__primary u-text-lg-1 u-text-md-1 u-text-sm-1 ${primarHighlighted}`}
          >
            <Text field={fields.PrimaryHeading} />
          </h1>
        );
      case 'H3':
        return (
          <h3
            tabIndex="0"
            className={`c-full-width-two-col-text-img__content__fields__primary u-text-lg-3 u-text-md-3 u-text-sm-3 ${primarHighlighted}`}
          >
            <Text field={fields.PrimaryHeading} />
          </h3>
        );
      case 'Div H1':
        return (
          <div
            tabIndex="0"
            className={`c-full-width-two-col-text-img__content__fields__primary u-text-lg-1 u-text-md-1 u-text-sm-1 ${primarHighlighted}`}
          >
            <Text field={fields.PrimaryHeading} />
          </div>
        );
      case 'Div H2':
        return (
          <div
            tabIndex="0"
            className={`c-full-width-two-col-text-img__content__fields__primary u-text-lg-2 u-text-md-2 u-text-sm-2 ${primarHighlighted}`}
          >
            <Text field={fields.PrimaryHeading} />
          </div>
        );
      case 'Div H3':
        return (
          <div
            tabIndex="0"
            className={`c-full-width-two-col-text-img__content__fields__primary u-text-lg-3 u-text-md-3 u-text-sm-3 ${primarHighlighted}`}
          >
            <Text field={fields.PrimaryHeading} />
          </div>
        );
      case 'H2':
      default:
        return (
          <h2
            tabIndex="0"
            className={`c-full-width-two-col-text-img__content__fields__primary u-text-lg-2 u-text-md-2 u-text-sm-2 ${primarHighlighted}`}
          >
            <Text field={fields.PrimaryHeading} />
          </h2>
        );
    }
  };

  return (
    <>
      {renderSubTitle({ field: fields.SubTitle, classes: subTitleClasses })}
      {renderFeaturedHeading({
        field: fields.FeatureTitle,
        classes:
          'c-full-width-two-col-text-img__content__fields__feature u-text-lg-5 u-text-md-5 u-text-sm-5',
      })}
      {renderPrimaryHeading(fields)}

      {fields.DisplayImageOption?.value && (
        <div
          className="c-full-width-two-col-text-img__content__fields__image"
          data-bg-image-small={smallBackgroundImageSrc}
          data-bg-image-medium={mediumBackgroundImageSrc}
          data-bg-image-large={largeBackgroundImageSrc}
          data-component="Image"
        ></div>
      )}

      {renderSecondaryHeading({
        field: fields.SecondaryHeading,
        classes: `c-full-width-two-col-text-img__content__fields__secondary ${secondaryHighlighted}`,
      })}
      {renderText({
        field: fields.Text,
        classes: 'c-full-width-two-col-text-img__content__fields__text',
      })}

      {renderFirstCta()}
      {renderCtaLinkTheme({
        field: fields.CallToActionSecondaryButton,
        tracking: secondaryTrackingAttributes,
        isInverted: true,
        classes: `${mapCtaSizeClass(
          fields.CTASecondarySize?.fields?.value?.value
        )} ${mapCtaThemeClass(
          isLight
            ? fields.CTASecondaryThemeLight?.fields?.value?.value
            : fields.CTASecondaryThemeDark?.fields?.value?.value,
          'c-button-Secondary'
        )}`,
      })}
    </>
  );
};
